import { useCallback, useRef, useState } from 'react';
import connectApi from '../../services/connectApi';
import { apiMessage } from './connect-api-message';

// Spec 2026-09-12 §6. Stripe calls fetchClientSecret once up front and again
// whenever a session expires mid-flow, so a surface needs a secret it can
// re-mint on its own. The surface — never a component list — is all the
// browser sends: the server decides what the session carries.
const useAccountSession = (companyId, surface) => {
  const [error, setError] = useState(null);
  // Read inside the callback so a re-render with a new company never leaves a
  // stale id baked into the function Stripe is holding.
  const latest = useRef({ companyId, surface });
  latest.current = { companyId, surface };

  const fetchSecret = useCallback(async () => {
    const { companyId: id, surface: name } = latest.current;
    if (!id) {
      const missing = new Error('Select a company to continue.');
      setError(missing.message);
      throw missing;
    }
    try {
      const session = connectApi.toAccountSession(
        await connectApi.createAccountSession(id, name),
      );
      if (!session) throw new Error('This Stripe session could not be started.');
      setError(null);
      return session.clientSecret;
    } catch (caught) {
      // Two shapes reach here. The controller's own refusal is a bare string
      // with 422; a BusinessRuleException ("This company has no Stripe account
      // yet") comes back 400 inside the `{ StatusCode, Message }` envelope the
      // exception middleware writes. apiMessage (connect-api-message.js) reads
      // exactly these three in this order, for exactly this reason — and also
      // strips a dashboard.stripe.com URL or a stack-trace-shaped body before
      // it can reach a restaurant's screen, which this hook's own inline
      // extraction never did.
      const message =
        apiMessage(caught) || caught?.message || 'This Stripe session could not be started.';
      setError(message);
      throw caught instanceof Error ? caught : new Error(message);
    }
  }, []);

  return { fetchSecret, error };
};

export default useAccountSession;
